import { useState } from "react";
import {
  Check,
  Star,
  ArrowRight,
  Info,
  Award,
  Trophy,
  Rocket,
  Sparkles,
  PiggyBank,
  Zap,
} from "lucide-react";
import type { BillingId, PlanId } from "@/config/checkoutConfig";
import { getCheckoutUrl } from "@/config/checkoutConfig";
import { PLANS, priceFor, currency } from "@/lib/pricing";

interface BillingOption {
  id: BillingId;
  label: string;
  headline: string;
  subheadline: string;
  bullets: string[];
  Icon: typeof Rocket;
  badge?: string;
  recommended?: boolean;
}

const BILLING_OPTIONS: BillingOption[] = [
  {
    id: "monthly",
    label: "Monthly",
    headline: "Start Today",
    subheadline: "Lowest upfront investment.",
    bullets: ["Start immediately", "Upgrade anytime", "Lowest commitment"],
    Icon: Rocket,
  },
  {
    id: "annual",
    label: "Annual",
    headline: "Best Value",
    subheadline: "Pay once. Save the most.",
    bullets: [
      "Save the most",
      "One payment",
      "Recommended for serious builders",
    ],
    Icon: PiggyBank,
    badge: "Recommended",
    recommended: true,
  },
  {
    id: "fivePay",
    label: "5 Easy Payments",
    headline: "Most Flexible",
    subheadline: "Spread your investment.",
    bullets: [
      "No large upfront payment",
      "Same transformation",
      "Payment flexibility",
    ],
    Icon: Zap,
    badge: "Most Flexible",
  },
];

const BILLING_MESSAGE: Record<BillingId, string> = {
  monthly:
    "Start building today with the lowest upfront investment. Perfect if you want to get started immediately.",
  annual:
    "Our best value. Pay once, save the most, and maximize your long-term return on your recurring income business.",
  fivePay:
    "Enjoy the benefits of Annual while spreading your investment across five easy payments — you receive the full program immediately.",
};

/** Dynamic CTA text based on the current plan + billing selection. */
function ctaLabel(plan: PlanId, billing: BillingId): string {
  const planLabel = plan === "elite" ? "ELITE" : "PREMIUM";
  if (billing === "monthly") return `Enroll in ${planLabel} Monthly`;
  if (billing === "annual") return `Enroll in ${planLabel} Annual & Save`;
  return `Start ${planLabel} with 5 Easy Payments`;
}

export default function Pricing() {
  // Defaults: ELITE (recommended plan) + MONTHLY (lowest perceived initial
  // investment). Users then explore Annual/5-pay to discover savings.
  const [plan, setPlan] = useState<PlanId>("elite");
  const [billing, setBilling] = useState<BillingId>("monthly");

  const billingSuffix =
    billing === "monthly"
      ? "/month"
      : billing === "annual"
      ? "/year"
      : " × 5 payments";

  const checkoutUrl = getCheckoutUrl(plan, billing);
  const selectedPlanObj = PLANS.find((p) => p.id === plan)!;

  return (
    <section
      id="pricing"
      data-testid="wc-pricing"
      className="bg-[#f8f6ff] py-20 sm:py-28"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        {/* Section header — copy preserved */}
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-white text-[#673dfc] border border-[#e5dfff] font-nav font-semibold text-xs rounded-full px-3 py-1 mb-4">
            Membership plans
          </div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#30265b] leading-[1.1]">
            Choose the Growth Accelerator that&apos;s right for your $10K/month goal.
          </h2>
          <p className="mt-5 text-lg text-gray-600 leading-relaxed">
            Choose your plan in 3 simple steps and start building your recurring income business today.
          </p>
        </div>

        {/* ---------- STEP 1: Choose the plan ---------- */}
        <div className="mt-14 text-center">
          <div className="inline-flex items-center gap-2 bg-[#30265b] text-[#d3fc3a] font-nav font-semibold text-xs rounded-full px-3 py-1 mb-3">
            Step 1 of 3
          </div>
          <h3 className="font-display font-bold text-2xl sm:text-3xl text-[#30265b] leading-tight">
            Choose your Growth Accelerator.
          </h3>
        </div>

        <div
          data-testid="wc-pricing-plan-picker"
          className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-stretch"
          role="radiogroup"
          aria-label="Choose your Growth Accelerator plan"
        >
          {PLANS.map((p) => {
            const isElite = p.featured;
            const selected = plan === p.id;
            const price = priceFor(p, billing);
            return (
              <button
                key={p.id}
                type="button"
                role="radio"
                aria-checked={selected}
                data-testid={`wc-pricing-card-${p.id}`}
                onClick={() => setPlan(p.id)}
                className={`group relative text-left rounded-3xl p-8 sm:p-10 flex flex-col h-full transition-all duration-300 cursor-pointer ${
                  isElite
                    ? selected
                      ? "bg-[#30265b] text-white border-2 border-[#d3fc3a] shadow-2xl shadow-[#673dfc]/40 lg:-translate-y-4"
                      : "bg-[#30265b] text-white border-2 border-[#673dfc]/40 shadow-xl shadow-[#673dfc]/15 lg:-translate-y-2 hover:border-[#d3fc3a]/60 hover:shadow-2xl hover:shadow-[#673dfc]/25"
                    : selected
                    ? "bg-white border-2 border-[#673dfc] shadow-xl shadow-[#673dfc]/15 -translate-y-1"
                    : "bg-white border border-gray-200 hover:border-[#c5b4ff] hover:-translate-y-0.5 hover:shadow-lg"
                }`}
              >
                {isElite && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#d3fc3a] text-[#1a1a24] text-xs font-display font-bold uppercase tracking-wider py-1.5 px-4 rounded-full shadow-md flex items-center gap-1.5">
                    <Star size={13} fill="#1a1a24" /> Most Popular
                  </div>
                )}

                {/* Selection state pill */}
                <div className="absolute top-6 right-6">
                  {selected ? (
                    <span
                      className={`inline-flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full ${
                        isElite
                          ? "bg-[#d3fc3a] text-[#1a1a24]"
                          : "bg-[#673dfc] text-white"
                      }`}
                    >
                      <Check size={11} strokeWidth={3} /> Selected
                    </span>
                  ) : (
                    <span
                      className={`text-[10px] font-nav font-semibold uppercase tracking-[0.14em] ${
                        isElite
                          ? "text-white/50 group-hover:text-[#d3fc3a]"
                          : "text-gray-400 group-hover:text-[#673dfc]"
                      }`}
                    >
                      Tap to select
                    </span>
                  )}
                </div>

                <div>
                  <div
                    className={`flex items-center gap-2 text-xs font-nav font-semibold uppercase tracking-[0.14em] ${
                      isElite ? "text-[#d3fc3a]" : "text-[#673dfc]"
                    }`}
                  >
                    {isElite ? <Trophy size={14} strokeWidth={2} /> : <Award size={14} strokeWidth={2} />}
                    {isElite ? "ELITE" : "PREMIUM"}
                  </div>
                  <div
                    className={`mt-2 font-display font-bold text-2xl sm:text-3xl leading-tight ${
                      isElite ? "text-white" : "text-[#30265b]"
                    }`}
                  >
                    {p.name}
                  </div>
                  <p
                    className={`mt-3 text-sm leading-relaxed ${
                      isElite ? "text-white/70" : "text-gray-600"
                    }`}
                  >
                    {p.tagline}
                  </p>
                </div>

                <div className="mt-6">
                  <div className="flex items-baseline gap-2">
                    <span
                      className={`font-display font-extrabold text-5xl tracking-tight ${
                        isElite ? "text-white" : "text-[#30265b]"
                      }`}
                    >
                      {currency(price)}
                    </span>
                    <span
                      className={`font-nav text-sm ${
                        isElite ? "text-white/70" : "text-gray-500"
                      }`}
                    >
                      {billingSuffix}
                    </span>
                  </div>
                  <div
                    className={`text-xs mt-1 ${
                      isElite ? "text-white/60" : "text-gray-500"
                    }`}
                  >
                    Monthly {currency(p.monthly)} · Annual{" "}
                    {currency(p.annual)} · 5-pay {currency(p.fivePay)}
                  </div>
                  {billing === "annual" && (
                    <div
                      key={`${p.id}-annual-save`}
                      className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-display font-bold wc-fade-up ${
                        isElite
                          ? "bg-[#d3fc3a] text-[#1a1a24]"
                          : "bg-[#e9ffb0] text-[#30265b]"
                      }`}
                    >
                      You save {currency(p.monthly * 12 - p.annual)} vs paying monthly
                    </div>
                  )}
                  {billing === "fivePay" && (
                    <div
                      key={`${p.id}-fivepay-note`}
                      className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-display font-semibold wc-fade-up ${
                        isElite
                          ? "bg-white/10 text-white"
                          : "bg-[#f3efff] text-[#30265b]"
                      }`}
                    >
                      Spread across 5 payments — full program starts immediately
                    </div>
                  )}
                </div>

                <div className="mt-6 space-y-3">
                  {p.bullets.map((b) => (
                    <div key={b} className="flex items-start gap-3">
                      <div
                        className={`w-5 h-5 rounded-full grid place-items-center flex-shrink-0 mt-0.5 ${
                          isElite ? "bg-[#d3fc3a] text-[#1a1a24]" : "bg-[#673dfc] text-white"
                        }`}
                      >
                        <Check size={12} strokeWidth={3} />
                      </div>
                      <div
                        className={`text-sm leading-relaxed ${
                          isElite ? "text-white/90" : "text-gray-700"
                        }`}
                      >
                        {b}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Simple expectation summary — full breakdown lives in the
                    dedicated "How We Win Together" section below. */}
                <div
                  className={`mt-6 rounded-2xl p-4 text-sm leading-relaxed ${
                    isElite
                      ? "bg-white/[0.06] border border-white/10 text-white/85"
                      : "border border-gray-200 bg-gray-50 text-[#30265b]"
                  }`}
                >
                  <span className="font-display font-semibold">
                    Your Role:
                  </span>{" "}
                  {isElite
                    ? "Reply to qualified lead inquiries, build relationships, support your prospects before and after Discovery Calls, and welcome your new subscribers."
                    : "Build relationships, reply to qualified lead inquiries, conduct your own Discovery Calls, and welcome your new subscribers."}
                </div>

                {/* What WinCampaign Handles — ELITE only */}
                {p.weHandle && (
                  <div className="mt-4 rounded-2xl bg-[#d3fc3a]/10 border border-[#d3fc3a]/40 p-4 text-sm text-white/85 leading-relaxed">
                    <span className="font-display font-semibold text-[#d3fc3a]">
                      WinCampaign Handles:
                    </span>{" "}
                    Marketing, content creation, lead generation, and
                    professionally conducting up to 10 Discovery Calls per
                    month on your behalf.
                  </div>
                )}

                {isElite && (
                  <div className="mt-6 rounded-2xl bg-white/5 border border-white/10 p-5 text-xs text-white/75 leading-relaxed">
                    <div className="flex items-center gap-2 text-[11px] font-nav font-semibold uppercase tracking-[0.14em] text-[#d3fc3a]">
                      <Info size={13} /> Dedicated Growth Advisor Compensation
                    </div>
                    <div className="mt-3 space-y-2.5">
                      <p>
                        Your dedicated Growth Advisor is rewarded only when
                        they help you gain a new subscriber. They receive a
                        one-time <span className="font-display font-semibold text-white">10% share</span> of your initial Brand Affiliate commission for any WinCampaign plan they successfully close during a Discovery Call conducted on your behalf.
                      </p>
                      <p>
                        Your Growth Advisor does not receive any recurring
                        commissions. You keep{" "}
                        <span className="font-display font-semibold text-white">
                          100% of all recurring monthly and annual Brand Affiliate commissions
                        </span>{" "}
                        generated by that subscriber — so you continue building long-term recurring income month after month.
                      </p>
                      <p className="font-display font-medium text-white/85">
                        This aligns your Growth Advisor&apos;s success with yours — they&apos;re rewarded for helping you acquire new subscribers, while your recurring income remains entirely yours.
                      </p>
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* ---------- STEP 2: How would you like to invest? ---------- */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-2 bg-[#30265b] text-[#d3fc3a] font-nav font-semibold text-xs rounded-full px-3 py-1 mb-3">
            Step 2 of 3
          </div>
          <div className="inline-flex items-center gap-2 bg-white text-[#673dfc] border border-[#e5dfff] font-nav font-semibold text-xs rounded-full px-3 py-1 mb-4 ml-2">
            <Sparkles size={13} /> Choose how you&apos;d like to invest
          </div>
          <h3 className="font-display font-bold text-2xl sm:text-3xl text-[#30265b] leading-tight">
            Which option feels right for you?
          </h3>
          <ul className="mt-5 max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-gray-700 text-left">
            {[
              { k: "Lowest upfront investment", v: "Monthly" },
              { k: "Best overall value", v: "Annual" },
              { k: "Flexibility without delay", v: "5 Easy Payments" },
            ].map((row) => (
              <li key={row.v} className="rounded-2xl bg-white border border-gray-200 px-4 py-3">
                <div className="text-xs font-nav text-gray-500">I want the…</div>
                <div className="font-display font-semibold text-[#30265b] mt-0.5">
                  {row.k}
                </div>
                <div className="mt-1 text-xs font-nav text-[#673dfc] font-semibold">
                  → {row.v}
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Buyer's guide billing selector (prices dynamic per selected plan) */}
        <div
          data-testid="wc-pricing-billing-toggle"
          className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4"
          role="radiogroup"
          aria-label="Choose your payment option"
        >
          {BILLING_OPTIONS.map((opt) => {
            const selected = billing === opt.id;
            const Icon = opt.Icon;
            const savings =
              opt.id === "annual"
                ? selectedPlanObj.monthly * 12 - selectedPlanObj.annual
                : 0;
            const billingPrice = priceFor(selectedPlanObj, opt.id);
            const priceLabel =
              opt.id === "monthly"
                ? `${currency(billingPrice)}/mo`
                : opt.id === "annual"
                ? `${currency(billingPrice)}/yr`
                : `${currency(billingPrice)} × 5`;
            return (
              <button
                key={opt.id}
                type="button"
                role="radio"
                aria-checked={selected}
                data-testid={`wc-pricing-billing-${opt.id}`}
                onClick={() => setBilling(opt.id)}
                className={`group relative text-left rounded-3xl p-6 border-2 transition-all duration-300 cursor-pointer ${
                  selected
                    ? opt.recommended
                      ? "bg-[#30265b] text-white border-[#d3fc3a] shadow-2xl shadow-[#673dfc]/30 -translate-y-1 md:scale-[1.03]"
                      : "bg-white text-[#1a1a24] border-[#673dfc] shadow-xl shadow-[#673dfc]/15 -translate-y-1"
                    : "bg-white text-[#1a1a24] border-gray-200 hover:border-[#c5b4ff] hover:-translate-y-0.5 hover:shadow-lg"
                }`}
              >
                {opt.badge && (
                  <div
                    className={`absolute -top-3 left-6 text-[10px] font-display font-bold uppercase tracking-[0.14em] py-1 px-3 rounded-full flex items-center gap-1 ${
                      opt.recommended
                        ? "bg-[#d3fc3a] text-[#1a1a24]"
                        : "bg-[#673dfc] text-white"
                    }`}
                  >
                    {opt.recommended && <Star size={10} fill="#1a1a24" />}
                    {opt.badge}
                  </div>
                )}

                <div className="flex items-start justify-between">
                  <div
                    className={`w-11 h-11 rounded-2xl grid place-items-center ${
                      selected && opt.recommended
                        ? "bg-[#d3fc3a] text-[#1a1a24]"
                        : selected
                        ? "bg-[#673dfc] text-white"
                        : "bg-[#f3efff] text-[#673dfc]"
                    }`}
                  >
                    <Icon size={20} strokeWidth={1.75} />
                  </div>
                  {selected ? (
                    <span
                      className={`inline-flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-[0.14em] px-2 py-1 rounded-full ${
                        opt.recommended
                          ? "bg-[#d3fc3a] text-[#1a1a24]"
                          : "bg-[#673dfc] text-white"
                      }`}
                    >
                      <Check size={11} strokeWidth={3} /> Selected
                    </span>
                  ) : (
                    <span className="text-[10px] font-nav text-gray-400 group-hover:text-[#673dfc]">
                      Tap to select
                    </span>
                  )}
                </div>

                <div
                  className={`mt-5 text-[11px] font-nav font-semibold uppercase tracking-[0.14em] ${
                    selected && opt.recommended
                      ? "text-[#d3fc3a]"
                      : "text-[#673dfc]"
                  }`}
                >
                  {opt.label}
                </div>
                <div
                  className={`mt-1 font-display font-bold text-xl leading-tight ${
                    selected && opt.recommended ? "text-white" : "text-[#30265b]"
                  }`}
                >
                  {opt.headline}
                </div>
                <div
                  className={`mt-1 text-sm font-nav ${
                    selected && opt.recommended
                      ? "text-[#c5b4ff]"
                      : "text-gray-600"
                  }`}
                >
                  {opt.subheadline}
                </div>

                {/* Dynamic price for the currently-selected plan */}
                <div
                  className={`mt-3 font-display font-extrabold text-2xl leading-none ${
                    selected && opt.recommended ? "text-white" : "text-[#30265b]"
                  }`}
                >
                  {priceLabel}
                </div>

                {savings > 0 && (
                  <div
                    className={`mt-2 inline-flex items-center gap-1.5 text-xs font-display font-bold px-2.5 py-1 rounded-full ${
                      selected && opt.recommended
                        ? "bg-[#d3fc3a] text-[#1a1a24]"
                        : "bg-[#e9ffb0] text-[#30265b]"
                    }`}
                  >
                    Save {currency(savings)}/yr
                  </div>
                )}

                {opt.id === "annual" && (
                  <div
                    data-testid="wc-pricing-annual-micro"
                    className="mt-3"
                  >
                    <div
                      className={`text-sm font-display font-semibold leading-snug ${
                        selected && opt.recommended
                          ? "text-white"
                          : "text-[#30265b]"
                      }`}
                    >
                      Only {currency(Math.round(billingPrice / 12))}/month when paid annually
                    </div>
                    <div
                      className={`text-xs font-nav leading-snug mt-0.5 ${
                        selected && opt.recommended
                          ? "text-white/70"
                          : "text-gray-500"
                      }`}
                    >
                      Less than {currency(Math.ceil(billingPrice / 365))}/day to build your recurring income business
                    </div>
                  </div>
                )}

                <ul
                  className={`mt-5 space-y-1.5 border-t pt-4 ${
                    selected && opt.recommended
                      ? "border-white/15"
                      : "border-gray-100"
                  }`}
                >
                  {opt.bullets.map((b) => (
                    <li
                      key={b}
                      className={`flex items-start gap-2 text-xs font-nav ${
                        selected && opt.recommended
                          ? "text-white/85"
                          : "text-gray-700"
                      }`}
                    >
                      <span
                        className={`w-3.5 h-3.5 rounded-full mt-0.5 grid place-items-center flex-shrink-0 ${
                          selected && opt.recommended
                            ? "bg-[#d3fc3a] text-[#1a1a24]"
                            : "bg-[#673dfc] text-white"
                        }`}
                      >
                        <Check size={8} strokeWidth={3} />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>

        {/* Dynamic supporting message */}
        <div
          key={billing}
          data-testid="wc-pricing-billing-message"
          className="mt-6 max-w-3xl mx-auto text-center rounded-2xl bg-white border border-[#e5dfff] px-5 py-4 wc-fade-up"
        >
          <div className="text-[11px] font-nav font-semibold uppercase tracking-[0.14em] text-[#673dfc] mb-1">
            Your selection
          </div>
          <p className="text-[#30265b] font-display font-medium text-sm sm:text-base leading-relaxed">
            {BILLING_MESSAGE[billing]}
          </p>
        </div>

        {/* ---------- STEP 3: Confirm & Checkout — direct SureCart ---------- */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-2 bg-[#30265b] text-[#d3fc3a] font-nav font-semibold text-xs rounded-full px-3 py-1 mb-3">
            Step 3 of 3
          </div>
          <h3 className="font-display font-bold text-2xl sm:text-3xl text-[#30265b] leading-tight">
            Confirm your plan &amp; checkout.
          </h3>
        </div>

        <div className="mt-8 max-w-2xl mx-auto rounded-3xl bg-[#30265b] text-white p-6 sm:p-10 shadow-2xl shadow-[#673dfc]/25">
          <div className="text-[11px] font-nav font-semibold uppercase tracking-[0.14em] text-[#d3fc3a] text-center">
            Your Selection
          </div>

          <dl
            data-testid="wc-pricing-summary"
            className="mt-6 divide-y divide-white/10 border-y border-white/10"
          >
            <div className="flex items-center justify-between py-4">
              <dt className="text-sm font-nav text-white/60 uppercase tracking-[0.12em]">
                Plan
              </dt>
              <dd
                data-testid="wc-pricing-summary-plan"
                className="font-display font-bold text-lg sm:text-xl text-white"
              >
                {plan === "elite" ? "ELITE" : "PREMIUM"}
              </dd>
            </div>
            <div className="flex items-center justify-between py-4">
              <dt className="text-sm font-nav text-white/60 uppercase tracking-[0.12em]">
                Payment
              </dt>
              <dd
                data-testid="wc-pricing-summary-billing"
                className="font-display font-bold text-lg sm:text-xl text-white"
              >
                {billing === "monthly"
                  ? "Monthly"
                  : billing === "annual"
                  ? "Annual"
                  : "5 Easy Payments"}
              </dd>
            </div>
            <div className="flex items-center justify-between py-4">
              <dt className="text-sm font-nav text-white/60 uppercase tracking-[0.12em]">
                Investment
              </dt>
              <dd
                data-testid="wc-pricing-summary-price"
                className="font-display font-extrabold text-2xl sm:text-3xl text-[#d3fc3a]"
              >
                {currency(priceFor(selectedPlanObj, billing))}
                {billing === "monthly"
                  ? "/month"
                  : billing === "annual"
                  ? "/year"
                  : " × 5 payments"}
              </dd>
            </div>
          </dl>

          <a
            href={checkoutUrl}
            data-testid="wc-pricing-cta"
            className="mt-8 w-full flex items-center justify-center gap-2 bg-[#d3fc3a] text-[#1a1a24] font-display font-bold text-base sm:text-lg rounded-full px-6 py-5 hover:-translate-y-1 hover:shadow-2xl hover:shadow-[#d3fc3a]/30 transition-transform duration-300 text-center leading-tight"
          >
            <Rocket size={19} />
            YES, I&apos;M ALL IN TO EARN MY $10K/MONTH
            <ArrowRight size={18} />
          </a>
        </div>
      </div>
    </section>
  );
}
